import {
  Component,
  OnInit,
  ElementRef,
  inject,
  viewChild,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { GenericState } from '../../store/generic.state';
import { GenericActions } from '../../store/generic.actions';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GenericService } from 'src/app/services/generic.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { JobListActions } from 'src/app/store/job-list/job-list.actions';
import * as _ from 'underscore';
import { AppCommon } from '../app.common';
import { lastValueFrom } from 'rxjs';
import { SITE_DATA } from 'src/app/productization/default.constant';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NgClass } from '@angular/common';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';

@Component({
    selector: 'app-registration-more',
    templateUrl: './registration-more.component.html',
    styleUrls: ['./registration-more.component.scss'],
    imports: [NzModalModule, NzSkeletonModule, MatFormField, MatInput, MatSuffix, NzTypographyModule, NzSpaceModule, NzButtonModule, FormsModule, ReactiveFormsModule, NgClass, NzSelectModule, NzInputModule]
})
export class RegistrationMoreComponent implements OnInit {
	private readonly store = inject(Store);
	private readonly destroyRef = inject(DestroyRef);
	private readonly genericService = inject(GenericService);
	private readonly nzMessage = inject(NzMessageService);

	private readonly genericResult$ = this.store.select(GenericState.getGeneric);
	public isShowRegistrationMore = false;
	public promptProperties: any;
	private userRole: any;
	public jobCategories: any[] = [];
	public availability: any[] = [];
	public workLocations: any[] = [];
	public sources: any[] = [];

	public siteData = SITE_DATA;

	public relocationOptions: string[] = [
		'I am willing to relocate',
		'I am NOT willing to relocate',
		'Depends on the salary'
	];

	public activeStep = -1;

	public step2Form: FormGroup;
	public step3Form: FormGroup;

	public acceptedFiles: string[] = ['.doc', '.docx', '.pdf'];
	public based64 = '';
	public fileName = '';

	public errMsgUpload = '';
	readonly fileUpload = viewChild<ElementRef>('file');

	private triggerOnce = false;

	async ngOnInit(): Promise<void> {
		localStorage.removeItem('selectedJrId');
		this.buildForm();
		this.genericResult$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (data) => {
			this.isShowRegistrationMore = data.modalRegistationMore;
			this.userRole = data.userRole;
			this.promptProperties = data.promptProperties;
			this.jobCategories = data.jobCategoriesWithSubOptions;
			this.availability = data.availability;
			this.workLocations = data.preferredWorkSetup;
			this.sources = data.sources;

			if (this.isShowRegistrationMore) {
				await this.showModalStep();
			}

			if (this.promptProperties) {
				this.patchForm();
				if (this.triggerOnce) return;
				this.triggerOnce = true;
			}
		});

		this.store.dispatch([
			new GenericActions.GetJobCategories(),
			new GenericActions.GetAvailability(),
			new GenericActions.GetPreferredWorkSetup(),
			new GenericActions.GetSources(),
			new GenericActions.GetJobCategoriesWithSubOptions()
		]);
	}

	private buildForm(): void {
		this.step2Form = new FormGroup({
			jobCategoryId: new FormControl('', [Validators.required]),
			workAvailabilityId: new FormControl('', [Validators.required]),
			preferredWorkLocationId: new FormControl('', [Validators.required]),
			isRelocationOption: new FormControl('', [Validators.required])
		});
		this.step3Form = new FormGroup({
			linkedInUrl: new FormControl('', [Validators.required]),
			sourceId: new FormControl('', [Validators.required]),
			whyWorkAtCloudstaff: new FormControl('', [Validators.required])
		});
	}

	private patchForm(): void {
		const jobCategory = this.jobCategories.find(
			(x) => x.option === this.promptProperties.jobCategory
		);
		const workAvailability = this.availability.find(
			(x) => x.option === this.promptProperties.workAvailability
		);
		const preferredWorkLocation = this.workLocations.find(
			(x) => x.option === this.promptProperties.preferredWorkLocation
		);
		const isRelocationOption = this.relocationOptions.find(
			(x) => x === this.promptProperties.isRelocationAnOption
		);
		const source = this.sources.find(
			(x) => x.option === this.promptProperties.source
		);
		this.step2Form.patchValue({
			jobCategoryId: jobCategory?.id,
			workAvailabilityId: workAvailability?.id,
			preferredWorkLocationId: preferredWorkLocation?.id,
			isRelocationOption: isRelocationOption
		});

		this.step3Form.patchValue({
			linkedInUrl: this.promptProperties.linkedInUrl,
			sourceId: source?.id,
			whyWorkAtCloudstaff: this.promptProperties.whyWorkAtCloudstaff
		});
	}

	private async showModalStep() {
		this.errMsgUpload = '';
		this.activeStep = -1;

		try {
			const data: any = await lastValueFrom(
				this.genericService.get('ApplicantProtected/GetSignInPromptProperties')
			);

			if (_.isEmpty(data)) return;

			// set local promptProperties from fresh API data
			this.promptProperties = data;

			// if all fields are filled, do not show modal
			if (Object.values(data).every(Boolean)) return;

			if (
				this.promptProperties.resumeUrl === null ||
				this.promptProperties.resumeUrl === ''
			) {
				this.activeStep = 1;
			} else if (
				(!this.promptProperties.jobCategory ||
					!this.promptProperties.workAvailability ||
					!this.promptProperties.preferredWorkLocation ||
					!this.promptProperties.isRelocationAnOption) &&
				!localStorage.getItem('skipModal2')
			) {
				this.activeStep = 2;
			} else if (
				(!this.promptProperties.linkedInUrl ||
					!this.promptProperties.whyWorkAtCloudstaff ||
					!this.promptProperties.source) &&
				!localStorage.getItem('skipModal3')
			) {
				this.activeStep = 3;
			} else {
				this.store.dispatch(new GenericActions.HideMoreModal());
			}
		} catch (error) {}
	}

	public handleCancel() {
		this.store.dispatch(new GenericActions.HideMoreModal());
		this.fileName = '';
		this.based64 = '';
		this.activeStep = 1;

		const fileUpload = this.fileUpload();
  if (fileUpload) {
			fileUpload.nativeElement.value = '';
		}
	}
	public handleOk() {
		this.store.dispatch(new GenericActions.HideMoreModal());
		this.activeStep = 1;
	}

	public nextStep(step: number) {
		if (step === 2 && this.based64) {
			this.processResume();
		}

		if (step === 3 && this.step2Form.valid) {
			this.processSecondForm();
		}

		if (step === 4 && this.step3Form.valid) {
			this.processThirdForm();
			this.handleOk();
			this.autoApply();
		}
	}

	private processResume(): void {
		if (!this.based64) return;

		const payload = {
			candidateId: this.userRole.profileId,
			filename: this.fileName,
			base64File: this.based64,
			url: ''
		};

		const id = this.nzMessage.loading('Uploading resume...', {
			nzDuration: 0
		}).messageId;

		this.genericService
			.post('ApplicantProtected/UploadResume', payload)
			.subscribe({
				next: () => {
					this.store.dispatch([
						new GenericActions.GetPromptProperties(),
						new GenericActions.HideMoreModal()
					]);

					this.nzMessage.remove(id);
					this.nzMessage.success('Resume uploaded.');

					this.fileName = '';
					this.based64 = '';

					const fileUpload = this.fileUpload();
     if (fileUpload) {
						fileUpload.nativeElement.value = '';
					}

					if (localStorage.getItem('skipModal2')) {
						this.autoApply();
					}

					if (this.step2Form.invalid) {
						this.activeStep = 2;
						this.store.dispatch(new GenericActions.ShowMoreModal());
					}

					if (this.step3Form.invalid) {
						this.activeStep = 3;
						this.store.dispatch(new GenericActions.ShowMoreModal());
					}
				},
				error: () => {
					this.nzMessage.remove(id);
					this.nzMessage.error('Error uploading resume.');
				},
				complete: () => {
					this.nzMessage.remove(id);
				}
			});
	}

	private processSecondForm(): void {
		const id = this.nzMessage.loading('Saving...', {
			nzDuration: 0
		}).messageId;

		this.genericService
			.post('ApplicantProtected/SaveAboutYourself', this.step2Form.value)
			.subscribe({
				next: () => {
					if (this.step3Form.invalid) {
						this.activeStep = 3;
						this.store.dispatch(new GenericActions.ShowMoreModal());
					}
					this.store.dispatch(new GenericActions.GetPromptProperties());
					this.nzMessage.remove(id);
					this.nzMessage.success('Successfully saved.');
					if (
						(this.promptProperties.linkedInUrl &&
							this.promptProperties.whyWorkAtCloudstaff &&
							this.promptProperties.source) ||
						localStorage.getItem('skipModal3')
					) {
						this.store.dispatch(new GenericActions.HideMoreModal());
						this.autoApply();
					}
				},
				error: () => {
					this.nzMessage.remove(id);
					this.nzMessage.error('Error saving.');
				},
				complete: () => {
					this.nzMessage.remove(id);
				}
			});
	}

	private processThirdForm(): void {
		const id = this.nzMessage.loading('Saving...', {
			nzDuration: 0
		}).messageId;

		const payload = {
			...this.step3Form.value,
			linkedInUrl: AppCommon.formatUrl(this.step3Form.value.linkedInUrl)
		};

		this.genericService
			.post('ApplicantProtected/SaveFewLastThings', payload)
			.subscribe({
				next: () => {
					this.store.dispatch([
						new GenericActions.GetPromptProperties(),
						new GenericActions.HideMoreModal()
					]);

					this.nzMessage.remove(id);
					this.nzMessage.success('Successfully saved.');
				},
				error: () => {
					this.nzMessage.remove(id);
					this.nzMessage.error('Error saving.');
				},
				complete: () => {
					this.nzMessage.remove(id);
				}
			});
	}

	public handleInputChange(e: any) {
		const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];

		/* Case:
			Has valid file select then open file selector 
			click cancel; handle undefined change of file object
		*/
		if (!file) return;

		const fileSize = file.size / 1024 / 1024;
		const fileName = String(file.name);
		const extIdx = fileName.lastIndexOf('.');
		const fileExt = fileName.substring(extIdx).toLowerCase();

		if (fileSize > 5) {
			this.errMsgUpload =
				'File size exceeds 5MB. The file is too large. Please upload a file smaller than 5MB.';
			this.fileUpload()!.nativeElement.value = '';
			this.based64 = '';
			return;
		}

		if (!this.acceptedFiles.includes(fileExt)) {
			this.errMsgUpload = `${this.fileName} invalid file format`;
			this.fileUpload()!.nativeElement.value = '';
			this.based64 = '';
			return;
		}

		const sanitized = fileName
			.substring(0, extIdx)
			.replace(/[^a-zA-Z0-9]/g, '');

		this.fileName = `${sanitized}${fileExt}`;

		const reader = new FileReader();
		reader.onload = this._handleReaderLoaded.bind(this);
		reader.readAsDataURL(file);
		this.errMsgUpload = '';
	}

	private _handleReaderLoaded(e: any) {
		const reader = e.target;
		this.based64 = reader.result;
	}

	public onBlur(property: string) {
		let prop = this.step3Form.get(property);
		prop?.setValue(prop?.value.sanitizeText());
		prop?.updateValueAndValidity();
	}

	public skip(data: number) {
		switch (data) {
			case 2:
				this.activeStep = 3;
				localStorage.setItem('skipModal2', 'true');
				break;
			case 3:
				this.activeStep = 4;
				localStorage.setItem('skipModal3', 'true');
				this.store.dispatch(new GenericActions.HideMoreModal());
				this.autoApply();

				break;
			default:
				break;
		}
	}

	private autoApply() {
		let jrId = localStorage.getItem('selectedJrId');
		if (jrId) {
			this.store.dispatch(
				new JobListActions.JobRequestApply(parseInt(jrId), false)
			);
		}
	}
}
